/**
 * Runtime enum mirrors for the admin surfaces.
 *
 * The *types* for every operator DTO now live in `@genex/contracts` and are re-exported
 * (type-only) from `./types.js` — so a contract change fails this console's typecheck
 * rather than drifting silently. What can't be a type-only import is the handful of
 * runtime **value** arrays the UI iterates (filter dropdowns, badge maps): importing the
 * contract's zod-derived `const` arrays would pull zod + `@genex/money` into the browser
 * bundle. So they are declared here as plain literals and pinned to the contract unions
 * with `satisfies` — the same drift-safety, none of the runtime weight.
 */
import type {
  AdminUserStatus,
  AdminKycDocumentKind,
  AdminTxKind,
  AdminTxStatus,
  GiftcardStatus,
  GiftcardFaceCurrency,
  GiftcardCategory,
  KycDocumentKind,
  OperatorSettableState,
} from './types.js';

/**
 * Every account state, including the two the console cannot set.
 *
 * Widened from three to five with the contract. `inactive` is the dormant state a desk uses
 * for an account it does not want live and does not want closed; `closed` is the customer's
 * own doing and is terminal. Both are here because a screen must never render an unknown
 * value, and a badge map missing a case is a blank chip beside a customer's name.
 */
export const USER_STATUSES = [
  'active',
  'suspended',
  'locked',
  'inactive',
  'closed',
] as const satisfies readonly AdminUserStatus[];

/**
 * The four an operator may actually write. `closed` is deliberately absent: closing is the
 * customer's decision, and a console button that could do it for them is a console that can
 * end an account nobody asked to end.
 */
export const OPERATOR_SETTABLE_STATES = [
  'active',
  'suspended',
  'inactive',
  'locked',
] as const satisfies readonly OperatorSettableState[];

/** What an identity document can be, in the order a reviewer usually meets them. */
export const KYC_DOCUMENT_TYPES = [
  'bvn_slip',
  'nin_slip',
  'passport',
  'drivers_licence',
  'voter_card',
  'selfie',
  'utility_bill',
] as const satisfies readonly KycDocumentKind[];

export const KYC_DOCUMENT_KINDS = ['bvn', 'nin'] as const satisfies readonly AdminKycDocumentKind[];

export const ADMIN_TX_KINDS = [
  'crypto_sell',
  'crypto_deposit',
  'bank_funding',
  'card_funding',
  'transfer_out',
  'bill_payment',
  'giftcard_redeem',
  'refund',
  'reversal',
] as const satisfies readonly AdminTxKind[];

export const ADMIN_TX_STATUSES = [
  'pending',
  'settled',
  'failed',
  'reversed',
] as const satisfies readonly AdminTxStatus[];

export const GIFTCARD_STATUSES = [
  'pending',
  'approved',
  'rejected',
] as const satisfies readonly GiftcardStatus[];

export const GIFTCARD_FACE_CURRENCIES = [
  'USD',
  'GBP',
  'EUR',
  'CAD',
  'AUD',
] as const satisfies readonly GiftcardFaceCurrency[];

export const GIFTCARD_CATEGORIES = [
  'shopping',
  'entertainment',
  'gaming',
  'other',
] as const satisfies readonly GiftcardCategory[];
